import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { RouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import generateDefaultProposalName from 'global/utils/generateDefaultProposalName';
import BulletTabsDelegate, { BulletTabStep } from 'store/general/bulletTabsDelegate';
import FavoriteProposalStepController from 'store/modules/initiate-quote/favoriteProposalStepController';
import { InitiateProposalInput, ProposalExperience } from 'store/modules/initiate-quote/types';
import BasicInfoStepController from './step-controllers/basicInfoStepController';
import DefaultProposalExperienceStepController from './step-controllers/proposalExperienceStepController';

export const DEFAULT_PROPOSAL_EXPERIENCES: ProposalExperience[] = ['SHELF', 'FAVORITE'];

interface DomainParams {
  groupId: string;
}

export default class DefaultInitiateProposalPageStore {
  public initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public bulletTabsDelegate: BulletTabsDelegate;
  public domainParams: DomainParams;

  // Step controllers
  public basicInfoStepController: BasicInfoStepController;
  public proposalExperienceStepController: DefaultProposalExperienceStepController;
  public favoriteProposalStepController: FavoriteProposalStepController;

  public get proposalExperience(): ProposalExperience | null {
    return this.proposalExperienceStepController.proposalExperience;
  }

  public get stepControllers(): BulletTabStep[] {
    const steps: BulletTabStep[] = [this.basicInfoStepController, this.proposalExperienceStepController];
    if (this.proposalExperience === 'FAVORITE') {
      steps.push(this.favoriteProposalStepController);
    }
    return steps;
  }

  private getProposalInput = (): InitiateProposalInput => {
    return this.basicInfoStepController.form.getFormDataObject() as InitiateProposalInput;
  };

  public async getInitialData(): Promise<void> {
    this.initiateQuoteFlowDelegate.setIsInitialDataLoading(true);

    await this.initiateQuoteFlowDelegate.getFlowInitialData();
    runInAction(() => {
      this.basicInfoStepController.form.populateFields({
        proposalName: generateDefaultProposalName(this.initiateQuoteFlowDelegate.groupModule.groupName),
      });
      this.initiateQuoteFlowDelegate.setIsInitialDataLoading(false);
    });
  }

  public exitFromFirstStep(): void {
    redirectToRoute(RouteV2Id.GROUP_HUB, { groupId: this.domainParams.groupId });
  }

  constructor(params: DomainParams) {
    this.domainParams = params;

    this.initiateQuoteFlowDelegate = new InitiateQuoteFlowDelegate({
      domainParams: { groupId: params.groupId, nextPageId: RouteV2Id.QUOTE_CHECKLIST },
    });
    this.basicInfoStepController = new BasicInfoStepController();
    this.proposalExperienceStepController = new DefaultProposalExperienceStepController(
      this.initiateQuoteFlowDelegate,
      this.getProposalInput,
    );
    this.favoriteProposalStepController = new FavoriteProposalStepController(
      this.initiateQuoteFlowDelegate,
      this.getProposalInput,
    );

    this.bulletTabsDelegate = new BulletTabsDelegate({
      getStepControllers: (): BulletTabStep[] => this.stepControllers,
      exitFromFirstStep: (): void => this.exitFromFirstStep(),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const DefaultInitiateProposalPageStoreContext = createContext(
  new DefaultInitiateProposalPageStore({
    groupId: '',
  }),
);

export const useDefaultInitiateProposalPageStore = (): DefaultInitiateProposalPageStore => {
  return useContext(DefaultInitiateProposalPageStoreContext);
};

export { DefaultInitiateProposalPageStoreContext };
